/**
 * src/components/legal/LegalMarkdown.tsx — web renderer for legal documents.
 *
 * The parse is shared with the mobile app in spirit (same grammar, same
 * degrade-to-plain-text rule); this file is only the presentation half.
 * Deliberately not dangerouslySetInnerHTML: the document text is
 * admin-authored, but rendering it as HTML would make a stored-XSS out of a
 * legal document, and there's no upside.
 */
import { Fragment, useMemo } from 'react';

type Block =
  | { kind: 'h1' | 'h2' | 'h3' | 'p'; text: string }
  | { kind: 'ul' | 'ol'; items: string[] }
  | { kind: 'rule' };

type Span = { text: string; bold?: boolean; italic?: boolean; href?: string };

/** Line-based block parse; sufficient for legal prose. */
export function parseBlocks(markdown: string): Block[] {
  const lines = (markdown ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let para: string[] = [];
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null;

  const flushPara = () => { if (para.length) { blocks.push({ kind: 'p', text: para.join(' ').trim() }); para = []; } };
  const flushList = () => { if (list?.items.length) blocks.push(list); list = null; };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); flushList(); continue; }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) { flushPara(); flushList(); blocks.push({ kind: 'rule' }); continue; }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading?.[1] && heading[2] !== undefined) {
      flushPara(); flushList();
      const levels = ['h1', 'h2', 'h3'] as const;
      blocks.push({ kind: levels[heading[1].length - 1] ?? 'h3', text: heading[2].trim() });
      continue;
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet?.[1] !== undefined) {
      flushPara();
      if (list?.kind !== 'ul') { flushList(); list = { kind: 'ul', items: [] }; }
      list.items.push(bullet[1]!.trim());
      continue;
    }

    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (numbered?.[1] !== undefined) {
      flushPara();
      if (list?.kind !== 'ol') { flushList(); list = { kind: 'ol', items: [] }; }
      list.items.push(numbered[1]!.trim());
      continue;
    }

    flushList();
    para.push(line.trim());
  }
  flushPara();
  flushList();
  return blocks;
}

/** Inline spans. Unmatched syntax stays literal — a clause must never vanish. */
export function parseSpans(text: string): Span[] {
  const out: Span[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|(?<!\*)\*([^*]+)\*(?!\*)|__([^_]+)__/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    if (m[1] !== undefined) out.push(m[2] !== undefined ? { text: m[1], href: m[2] } : { text: m[1] });
    else if (m[3] !== undefined) out.push({ text: m[3], bold: true });
    else if (m[4] !== undefined) out.push({ text: m[4], italic: true });
    else if (m[5] !== undefined) out.push({ text: m[5], bold: true });
    last = re.lastIndex;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out.length ? out : [{ text }];
}

function Inline({ text }: { text: string }) {
  const spans = useMemo(() => parseSpans(text), [text]);
  return (
    <>
      {spans.map((s, i) =>
        s.href ? (
          <a key={i} href={s.href} target="_blank" rel="noreferrer noopener">{s.text}</a>
        ) : (
          <span key={i} style={{ fontWeight: s.bold ? 600 : undefined, fontStyle: s.italic ? 'italic' : undefined }}>
            {s.text}
          </span>
        ),
      )}
    </>
  );
}

export function LegalMarkdown({ source }: { source: string }) {
  const blocks = useMemo(() => parseBlocks(source), [source]);
  return (
    <div style={{ fontSize: 15, lineHeight: 1.6 }}>
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'rule':
            return <hr key={i} style={{ border: 0, borderTop: '1px solid var(--border)', margin: '18px 0' }} />;
          case 'ul':
            return <ul key={i}>{b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}</ul>;
          case 'ol':
            return <ol key={i}>{b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}</ol>;
          case 'h1':
            return <h1 key={i} style={{ fontSize: 24, marginTop: 22 }}><Inline text={b.text} /></h1>;
          case 'h2':
            return <h2 key={i} style={{ fontSize: 19, marginTop: 20 }}><Inline text={b.text} /></h2>;
          case 'h3':
            return <h3 key={i} style={{ fontSize: 16, marginTop: 16 }}><Inline text={b.text} /></h3>;
          default:
            return <Fragment key={i}><p><Inline text={b.text} /></p></Fragment>;
        }
      })}
    </div>
  );
}
